import { Bold, Heading2, Italic, List, ListOrdered } from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useRef, useState } from 'react'

/** Editor de texto con barra de formato. Guarda Markdown plano (que el modelo
 *  entiende tal cual) y muestra una vista previa con el subconjunto que
 *  soportamos: títulos, negrita, cursiva y listas. La vista previa se arma con
 *  elementos de React, nunca con HTML crudo. */
export function MarkdownEditor({
  id,
  value,
  onChange,
  maxLength,
  rows = 8,
  placeholder,
  required,
}: {
  id: string
  value: string
  onChange: (value: string) => void
  maxLength?: number
  rows?: number
  placeholder?: string
  required?: boolean
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const [preview, setPreview] = useState(false)

  function applyEdit(next: string, selectionStart: number, selectionEnd: number) {
    onChange(next)
    requestAnimationFrame(() => {
      ref.current?.focus()
      ref.current?.setSelectionRange(selectionStart, selectionEnd)
    })
  }

  function wrapSelection(marker: string, fallback: string) {
    const el = ref.current
    if (!el) return
    const { selectionStart: start, selectionEnd: end } = el
    const selected = value.slice(start, end) || fallback
    const next = value.slice(0, start) + marker + selected + marker + value.slice(end)
    const cursorStart = start + marker.length
    applyEdit(next, cursorStart, cursorStart + selected.length)
  }

  function prefixLines(prefixFor: (index: number) => string) {
    const el = ref.current
    if (!el) return
    const { selectionStart: start, selectionEnd: end } = el
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    const lineEndIdx = value.indexOf('\n', end)
    const lineEnd = lineEndIdx === -1 ? value.length : lineEndIdx
    const block = value.slice(lineStart, lineEnd)
    const prefixed = block
      .split('\n')
      .map((line, index) => `${prefixFor(index)}${line}`)
      .join('\n')
    const next = value.slice(0, lineStart) + prefixed + value.slice(lineEnd)
    applyEdit(next, lineStart, lineStart + prefixed.length)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (!(event.ctrlKey || event.metaKey)) return
    const key = event.key.toLowerCase()
    if (key === 'b') {
      event.preventDefault()
      wrapSelection('**', 'texto')
    } else if (key === 'i') {
      event.preventDefault()
      wrapSelection('*', 'texto')
    }
  }

  const count = value.length

  return (
    <div className="md-editor">
      <div className="md-editor__toolbar" role="toolbar" aria-label="Formato del texto">
        <button
          type="button"
          className="icon-button icon-button--neutral"
          onClick={() => wrapSelection('**', 'texto')}
          disabled={preview}
          aria-label="Negrita (Ctrl+B)"
          title="Negrita (Ctrl+B)"
        >
          <Bold size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="icon-button icon-button--neutral"
          onClick={() => wrapSelection('*', 'texto')}
          disabled={preview}
          aria-label="Cursiva (Ctrl+I)"
          title="Cursiva (Ctrl+I)"
        >
          <Italic size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="icon-button icon-button--neutral"
          onClick={() => prefixLines(() => '## ')}
          disabled={preview}
          aria-label="Título"
          title="Título"
        >
          <Heading2 size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="icon-button icon-button--neutral"
          onClick={() => prefixLines(() => '- ')}
          disabled={preview}
          aria-label="Lista con viñetas"
          title="Lista con viñetas"
        >
          <List size={14} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="icon-button icon-button--neutral"
          onClick={() => prefixLines((index) => `${index + 1}. `)}
          disabled={preview}
          aria-label="Lista numerada"
          title="Lista numerada"
        >
          <ListOrdered size={14} aria-hidden="true" />
        </button>

        <div className="md-editor__modes" role="group" aria-label="Modo">
          <button
            type="button"
            className="button button--ghost button--small"
            aria-pressed={!preview}
            onClick={() => setPreview(false)}
          >
            Escribir
          </button>
          <button
            type="button"
            className="button button--ghost button--small"
            aria-pressed={preview}
            onClick={() => setPreview(true)}
          >
            Vista previa
          </button>
        </div>
      </div>

      {preview ? (
        <div className="md-editor__preview" aria-label="Vista previa">
          {value.trim() ? (
            renderMarkdown(value)
          ) : (
            <p className="muted">Nada que mostrar todavía.</p>
          )}
        </div>
      ) : (
        <textarea
          id={id}
          ref={ref}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          required={required}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
      )}

      {maxLength !== undefined && (
        <span className="mono muted md-editor__count">
          {count}/{maxLength}
        </span>
      )}
    </div>
  )
}

const INLINE_PATTERN = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g

function renderInline(text: string): ReactNode[] {
  return text.split(INLINE_PATTERN).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={index}>{part.slice(1, -1)}</em>
    }
    return part
  })
}

/** Subconjunto de Markdown: `#`/`##`/`###`, `- ` y `1. ` para listas, párrafos
 *  separados por líneas en blanco, y **negrita** / *cursiva* en línea. */
export function renderMarkdown(source: string): ReactNode[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let paragraph: string[] = []
  let list: { ordered: boolean; items: string[] } | null = null

  function flushParagraph() {
    if (!paragraph.length) return
    blocks.push(<p key={`p-${blocks.length}`}>{renderInline(paragraph.join(' '))}</p>)
    paragraph = []
  }

  function flushList() {
    if (!list) return
    const items = list.items.map((item, index) => <li key={index}>{renderInline(item)}</li>)
    blocks.push(
      list.ordered ? (
        <ol key={`l-${blocks.length}`}>{items}</ol>
      ) : (
        <ul key={`l-${blocks.length}`}>{items}</ul>
      ),
    )
    list = null
  }

  for (const line of lines) {
    const heading = /^(#{1,3})\s+(.*)$/.exec(line)
    const bullet = /^[-*]\s+(.*)$/.exec(line)
    const numbered = /^\d+\.\s+(.*)$/.exec(line)

    if (!line.trim()) {
      flushParagraph()
      flushList()
    } else if (heading) {
      flushParagraph()
      flushList()
      const [, hashes = '', title = ''] = heading
      const Tag = hashes.length === 1 ? 'h3' : hashes.length === 2 ? 'h4' : 'h5'
      blocks.push(<Tag key={`h-${blocks.length}`}>{renderInline(title)}</Tag>)
    } else if (bullet || numbered) {
      flushParagraph()
      const ordered = Boolean(numbered)
      const text = (bullet ?? numbered)?.[1] ?? ''
      if (!list || list.ordered !== ordered) {
        flushList()
        list = { ordered, items: [] }
      }
      list.items.push(text)
    } else {
      flushList()
      paragraph.push(line.trim())
    }
  }
  flushParagraph()
  flushList()
  return blocks
}
